/**
 * The housing that all menus share.
 *
 * Head with title and closing cross, body, the buttons at the bottom. Before,
 * this stood six times in the HTML, each time slightly different — here it
 * stands once, and a new menu inherits behaviour that is already right:
 * positioning under the button, closing on a click beside it and on Escape.
 */

import type { ComponentChildren, RefObject } from 'preact'
import { useRef } from 'preact/hooks'
import { Icon } from './icons.js'
import { useAnchored, useDismiss } from './hooks.js'

export function Panel({
  open,
  title,
  anchor,
  onClose,
  children,
  actions,
  note,
}: {
  open: boolean
  title: string
  /**
   * The button the menu hangs under.
   *
   * Left out, the panel stands in the middle of the screen instead. Not every
   * card belongs to a button: the result of a photograph is the answer to
   * something that happened a screen ago, and hanging it under whatever
   * button is still around would put it somewhere arbitrary.
   */
  anchor?: RefObject<HTMLElement>
  onClose: () => void
  children?: ComponentChildren
  actions?: ComponentChildren
  /** A line under the buttons, when there is something to explain. */
  note?: string
}) {
  const panel = useRef<HTMLDivElement>(null)
  const none = useRef<HTMLElement>(null)
  useAnchored(open, anchor ?? none, panel)
  useDismiss(open, onClose, panel, anchor ?? none)

  if (!open) return null

  return (
    <div
      ref={panel}
      class={`docs${anchor ? '' : ' middle'}`}
      role="dialog"
      aria-label={title}
    >
      <div class="docs-head">
        <span class="docs-title">{title}</span>
        <button class="helper-close" onClick={onClose} aria-label="Schließen">
          <Icon name="x" />
        </button>
      </div>
      {children}
      {actions && <div class="docs-actions">{actions}</div>}
      {note && <p class="docs-note">{note}</p>}
    </div>
  )
}

/** A button in the foot of a menu. */
export function Action({
  icon,
  children,
  onClick,
  danger,
}: {
  icon?: Parameters<typeof Icon>[0]['name']
  children: ComponentChildren
  onClick: () => void
  danger?: boolean
}) {
  return (
    <button class={`action${danger ? ' danger' : ''}`} onClick={onClick}>
      {icon && <Icon name={icon} />}
      {children}
    </button>
  )
}

/**
 * A switch, as it stands in the checking menu and in the paper menu.
 *
 * The label encloses the box, so that the click on the word counts just as
 * much as the one on the box — on the phone that is the difference between
 * hitting it and tapping beside it.
 */
export function Toggle({
  checked,
  onChange,
  disabled,
  children,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  children: ComponentChildren
}) {
  return (
    <label class={`paper-print${disabled ? ' disabled' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange((e.currentTarget as HTMLInputElement).checked)}
      />
      <span>{children}</span>
    </label>
  )
}
